import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import styles from './quiz-loading.module.scss';
import classNames from 'classnames';
import QuizInfoTemplate from 'src/views/Quiz/QuizInfoTemplate/QuizInfoTemplate';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import WeightLossGraph from '../../WeightLossGraph';
import HTMLReactParser from 'html-react-parser';
import { isMosh, isMoshy } from 'src/env.mjs';

const QUIZ_LOADING_ANIMATION_LENGTH = 4000;

type QuizLoadingProps = {
    type?: 'default' | 'loading-graph' | 'loading-image';
    onResetTimeout?: () => void;
};

const QuizLoading = ({ type = 'default' }: QuizLoadingProps) => {
    const { currentQuestion, handleSubmitAnswer } = useContext(QuizContext);
    const {
        questionText,
        questionExplanation = '',
        questionImage,
        userInfo,
        additionalField1: halfwayMonth,
        additionalField2: endMonth,
        additionalField3: halfwayWeightPercentage,
        additionalField4: endWeightPercentage,
        estimateTimeText: autoContinue,
        legalText,
    } = currentQuestion || {};
    const leftAlignedContent = !!legalText;
    const richExplanation = !!legalText;

    const [continueDisabled, setContinueDisabled] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const animationLength = useMemo(
        () =>
            !autoContinue
                ? QUIZ_LOADING_ANIMATION_LENGTH
                : Number.isNaN(Number(autoContinue)) ||
                    Number(autoContinue) === 0
                  ? 999999
                  : autoContinue
                    ? parseInt(autoContinue)
                    : QUIZ_LOADING_ANIMATION_LENGTH,
        [autoContinue],
    );

    const handleSubmit = useCallback(() => {
        if (currentQuestion.questionId && !continueDisabled) {
            setContinueDisabled(true);
            // Submit answer
            handleSubmitAnswer({
                questionId: currentQuestion.questionId,
                questionText: currentQuestion.questionText,
            });
        }
    }, [currentQuestion, continueDisabled, handleSubmitAnswer]);

    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit]);

    // Auto submit
    useEffect(() => {
        if (animationLength > 0) {
            timeoutRef.current = setTimeout(() => {
                handleSubmit();
            }, animationLength);
        }

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [handleSubmit, animationLength]);

    const handlePopState = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    useEffect(() => {
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    return (
        <QuizInfoTemplate contentClassName={styles['quiz--loading-content']}>
            <div className={classNames(styles['quiz--section-break'])}>
                <h3
                    className={classNames('text-center', {
                        'text-white': isMosh,
                        'text-blue-extra-dark': isMoshy,
                    })}
                >
                    {questionText}
                </h3>
                {type === 'loading-graph' ? (
                    <WeightLossGraph
                        startWeight={userInfo?.weight ?? 0}
                        halfwayMonth={Number(halfwayMonth)}
                        endMonth={Number(endMonth)}
                        halfwayWeightPercentage={Number(
                            halfwayWeightPercentage,
                        )}
                        endWeightPercentage={Number(endWeightPercentage)}
                    />
                ) : type === 'default' && !!questionImage ? (
                    leftAlignedContent ? (
                        <div className={styles['image-wrapper--left-aligned']}>
                            <img
                                className={styles['image']}
                                src={questionImage}
                                alt={questionText}
                            />
                        </div>
                    ) : (
                        <div className={styles['icon-wrapper']}>
                            <img
                                className={styles['icon']}
                                src={questionImage}
                                alt={questionText}
                            />
                        </div>
                    )
                ) : null}
                <span
                    className={classNames(
                        styles['section-wrapper'],
                        leftAlignedContent &&
                            styles['section-wrapper--left-aligned'],
                    )}
                >
                    <span className={classNames(styles['subtitle'], 'p1')}>
                        {richExplanation
                            ? HTMLReactParser(questionExplanation)
                            : questionExplanation}
                    </span>
                </span>
                {type === 'loading-image' && (
                    <div className={styles['image-wrapper']}>
                        <img
                            className={styles['image']}
                            src={questionImage}
                            alt={questionText}
                        />
                    </div>
                )}
            </div>
        </QuizInfoTemplate>
    );
};

export default QuizLoading;
